import React, { useEffect, useMemo, useRef, useState } from 'react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import FullCalendar from '@fullcalendar/react';
import {
  Backdrop,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Container,
  Typography,
  CircularProgress,
} from '@mui/material';
import COLORS from 'theme/colors';
import { useAppDispatch } from 'store/hooks';
import {
  getAppointmentView,
  getProfilesByLocation,
  getProviderOfficeLocation,
  getProviders,
  saveAppointment,
} from 'store/slices/patientInfoSlice';
import { toast } from 'react-toastify';
import EditAppointment, { EditAppointmentProps } from './EditAppointment';
import NewAppointment, { NewAppointmentProps } from './NewAppointment';
type AppointmentEvent = {
  title: string;
  start: Date | string;
  end: Date | string;
  extendedProps: {
    slotType: string;
    provider?: string;
    profile?: string;
    shortNameProfile?: string;
    shortNameProvider?: string;
    reason?: string;
    status?: string;
    duration?: number;
    location?: string;
    apptIsBreak?: boolean;
  };
  [key: string]: any;
};
const AppointmentCalendar = (props: any) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [calendarView, setCalendarView] = useState<'timeGridDay' | 'timeGridWeek'>('timeGridWeek');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  const dispatch = useAppDispatch();
  const [officeId, setOfficeId] = useState<number | ''>('');
  const [profile, setProfile] = useState<number | ''>('');
  const [provider, setProvider] = useState<number | ''>('');

  const [officeList, setOfficeList] = useState<any[]>([]);
  const [providerList, setProviderList] = useState<any[]>([]);
  const [profileList, setProfileList] = useState<any[]>([]);

  const [weekRange, setWeekRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [appointments, setAppointments] = useState<AppointmentEvent[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  // 🔹 Multi-select filter
  const [slotTypeFilter, setSlotTypeFilter] = useState<string[]>(['FREE', 'BLOCKED']);

  const SLOT_TYPES = [
    { value: 'FREE', label: 'Free Slots', color: '#008000' },
    { value: 'OCCUPIED', label: 'Booked Slots', color: '#1f1bf3' },
  ];

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openNewAppointment, setOpenNewAppointment] = useState(false);
  const [editAppointment, setEditAppointment] = useState<EditAppointmentProps | null>(null);
  const [newAppointment, setNewAppointment] = useState<NewAppointmentProps | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [newLoading, setNewLoading] = useState(false);

  const handleEventClick = (info: any) => {
    const event = info.event;
    const slotType = event.extendedProps?.slotType;
    // 👉 Only for OCCUPIED slots
    // if (slotType === 'OCCUPIED') {
    //   setEditLoading(true); // 🔥 start loader
    //   const payload: EditAppointmentProps = {
    //     appointmentId: Number(event.id),
    //     providers: providerList,
    //     offices: officeList,
    //     profiles: profileList,
    //   };
    //   setEditAppointment(payload);
    //   setOpenEditDialog(true);
    // } else

    if (slotType !== 'FREE') {
      return;
    }
    setNewLoading(true); // 🔥 start loader
    const payload: NewAppointmentProps = {
      dateTime: event.start,
      duration: event.extendedProps?.duration || 0,
      profileName: event.extendedProps?.profile || '',
      providerId: provider || 0,
      profileId: profile || 0,
      examRoom: event.extendedProps?.examRoom || 0,
      officeId: officeId || 0,
      providers: providerList,
      offices: officeList,
    };
    if (!provider || !officeId || !profile) {
      console.error('Missing required data for creating appointment:', {
        provider,
        officeId,
        profile,
      });
      toast.error('Please select Office, Appointment Template and Provider before creating an appointment');
      return;
    }
    setNewAppointment(payload);
    setOpenNewAppointment(true);
  };
  /* ============================================================
     FILTERED EVENTS
  ============================================================ */

  const filteredAppointments = useMemo(() => {
    if (!slotTypeFilter.length) return appointments;

    return appointments.filter((event) => {
      const type = event?.extendedProps?.slotType?.trim()?.toUpperCase();

      return slotTypeFilter.includes(type);
    });
  }, [appointments, slotTypeFilter]);

  const slotTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {
      FREE: 0,
      BLOCKED: 0,
      OCCUPIED: 0,
    };

    appointments.forEach((event) => {
      const type = event?.extendedProps?.slotType?.trim()?.toUpperCase();
      if (type && counts[type] !== undefined) {
        counts[type]++;
      }
    });

    return counts;
  }, [appointments]);

  const getProviderOffices = () => {
    setOfficeList([]);
    dispatch(getProviderOfficeLocation({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          // Handle successful response
          setOfficeList(response.data);
        }
      });
  };

  const getProvider = (officeLoc: number, profileId: number) => {
    setProviderList([]);
    const data = {
      officeId: officeLoc,
      profile: profileId,
    };
    dispatch(getProviders(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          // Handle successful response
          setProviderList(response.data);
        }
      });
  };

  const getProfile = (officeId: number) => {
    setProfileList([]);
    dispatch(getProfilesByLocation(officeId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          // Handle successful response
          setProfileList(response.data);
        }
      });
  };

  const handleMouseEnter = (info: any) => {
    if (info.event.extendedProps.apptIsBreak) {
      info.el.setAttribute(
        'Title',
        `${info.event.title}
        Provider: ${info.event.extendedProps.provider}
        Duration: ${info.event.extendedProps.duration} minutes
        Start: ${dayjs.utc(info.event.start).format('MM/DD/YYYY hh:mm A')}
        End: ${dayjs.utc(info.event.end).format('MM/DD/YYYY hh:mm A')}
        Location: ${info.event.extendedProps.location}`
      );
    } else {
      info.el.setAttribute(
        'Title',
        `${info.event.title}
          Appointment Template: ${info.event.extendedProps.profile}
          Provider: ${info.event.extendedProps.provider}
          Location: ${info.event.extendedProps.location}
          Reason: ${info.event.extendedProps.reason}
          Status: ${info.event.extendedProps.status}
          Duration: ${info.event.extendedProps.duration} minutes
          Start: ${dayjs.utc(info.event.start).format('MM/DD/YYYY hh:mm A')}
          End: ${dayjs.utc(info.event.end).format('MM/DD/YYYY hh:mm A')}`
      );
    }
  };
  const handleFilterChange = (setter: any) => (e: any) => {
    setter(Number(e.target.value));
  };

  const getAppointmentWeekView = async () => {
    if (!(weekRange && selectedDate && officeId && profile && provider)) {
      return;
    }
    setAppointments([]); // optional
    setAppointmentsLoading(true);

    const payload = {
      officeId,
      profileId: profile,
      doctors: provider,
      weekStartDate: weekRange.startDate,
      weekEndDate: weekRange.endDate,
    };

    try {
      const response = await dispatch(getAppointmentView(payload)).unwrap();
      if (response?.status === 200) {
        // const formattedAppointments = response.data.map((appt: any) => {
        //   return {
        //     ...appt,
        //     start: dayjs(appt.weekStartDate).toDate(),
        //     end: dayjs(appt.weekEndDate).toDate(),
        //   };
        // });
        setAppointments(response.data);
        if (response.data.length === 0) {
          toast.info('No appointments found for the selected criteria');
        }
      } else {
        toast.error('Failed to fetch appointments');
      }
    } catch (err) {
      toast.error('Failed to fetch appointments');
    } finally {
      setAppointmentsLoading(false); // ✅ ONLY here
    }
  };

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(calendarView);
    }
  }, [calendarView]);

  useEffect(() => {
    setProviderList([]);
    setProfileList([]);
    setProvider('');
    setProfile('');
    getProviderOffices();
  }, []); // ✅ officeList.length added here

  useEffect(() => {
    if (officeId) {
      setProviderList([]);
      setProfileList([]);
      setProvider('');
      setProfile('');
      getProfile(officeId);
    }
  }, [officeId]);

  useEffect(() => {
    if (officeId && profile) {
      setProviderList([]);
      setProvider('');
      getProvider(officeId, profile);
    }
  }, [officeId, profile]);

  useEffect(() => {
    if (officeId && profile && provider && weekRange) {
      getAppointmentWeekView();
    }
  }, [weekRange]);

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={appointmentsLoading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='lg'>
        <Grid item justifyContent={'left'}>
          <Typography variant='h6' fontWeight={600} mb={2}>
            Doctor Appointment Calendar
          </Typography>

          <Grid container spacing={2} alignItems='center' mb={2}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label='Office Location'
                value={officeId}
                onChange={(e) => {
                  setOfficeId(Number(e.target.value));
                  setProfile('');
                }}
              >
                <MenuItem value=''>Please Select</MenuItem>
                {officeList.length === 0 && <CircularProgress size={20} />}
                {officeList.map((office) => (
                  <MenuItem key={office.id} value={office.id}>
                    {office.city}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label='Appointment Template'
                value={profile}
                disabled={!officeId}
                onChange={(e) => setProfile(Number(e.target.value))}
              >
                <MenuItem value=''>Please Select</MenuItem>
                {profileList.length === 0 && <CircularProgress size={20} />}
                {profileList.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label='Provider'
                value={provider}
                disabled={!officeId}
                onChange={handleFilterChange(setProvider)}
              >
                <MenuItem value=''>Please Select</MenuItem>
                {providerList.length === 0 && <CircularProgress size={20} />}
                {providerList.map((prov) => (
                  <MenuItem key={prov.id} value={prov.id}>
                    {prov.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              {/* Calendar Icon */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label='Select Date'
                  value={selectedDate}
                  onChange={(newValue) => {
                    if (!newValue || !newValue.isValid() || !calendarRef.current) return;
                    setSelectedDate(newValue);
                    const api = calendarRef.current.getApi();
                    api.gotoDate(newValue.toDate());
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Fetch Appointments */}
            <Grid item>
              <Button
                variant='outlined'
                size='small'
                disabled={appointmentsLoading}
                onClick={getAppointmentWeekView}
              >
                {appointmentsLoading ? 'Loading...' : 'Go'}
              </Button>
            </Grid>
            {/* 🔹 Multi Slot Type Filter */}
            <Grid item xs={12} md={6}>
              {/* 🔹 Slot Type Filter (Checkboxes) */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {SLOT_TYPES.map((type) => (
                  <Box
                    key={type.value}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox
                      size='small'
                      checked={slotTypeFilter.includes(type.value)}
                      onChange={() => {
                        setSlotTypeFilter((prev) =>
                          prev.includes(type.value)
                            ? prev.filter((v) => v !== type.value)
                            : [...prev, type.value]
                        );
                      }}
                    />
                    <Typography variant='body2' color={type.color}>
                      {type.label}
                    </Typography>
                    {/* 🔹 COUNT BADGE */}
                    <Box
                      sx={{
                        backgroundColor: '#E3F2FD',
                        px: 1,
                        color: type.color,
                        borderRadius: 2,
                        fontSize: 12,
                        minWidth: 24,
                        textAlign: 'center',
                      }}
                    >
                      {slotTypeCounts[type.value] || 0}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
            <Box position='relative'>
              <FullCalendar
                timeZone={userTimezone}
                slotMinTime='08:00:00'
                slotMaxTime='18:00:00'
                expandRows
                height={1300}
                ref={calendarRef}
                plugins={[timeGridPlugin, interactionPlugin]}
                initialView='timeGridWeek'
                headerToolbar={{
                  left: 'prev',
                  center: 'title',
                  right: 'next',
                }}
                events={filteredAppointments}
                eventContent={(arg) => {
                  const { event, timeText } = arg;
                  const slotType = event.extendedProps.slotType;
                  const profile = event.extendedProps.shortNameProfile;
                  const provider = event.extendedProps.shortNameProvider;
                  const reason = event.extendedProps.reason;

                  return (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          padding: '4px 6px',
                          fontSize: 11,
                          overflow: 'hidden',
                        }}
                      >
                        {/* TOP ROW */}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontWeight: 600,
                            gap: 6,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          {/* LEFT SIDE */}
                          <div
                            style={{
                              display: 'flex',
                              gap: 6,
                              overflow: 'hidden',
                            }}
                          >
                            <span>{timeText}</span>

                            {provider && <span style={{ opacity: 0.8 }}>{provider}</span>}

                            {profile && <span style={{ opacity: 0.7 }}>{profile}</span>}
                          </div>
                        </div>

                        {/* TITLE ROW */}
                        <div
                          style={{
                            marginTop: 2,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {event.title}
                        </div>
                      </div>
                    </>
                  );
                }}
                eventDisplay='block'
                eventMouseEnter={handleMouseEnter}
                eventClick={handleEventClick}
                datesSet={(info) => {
                  const start = dayjs.utc(info.start).format('YYYY-MM-DD');
                  const end = dayjs.utc(info.end).subtract(1, 'day').format('YYYY-MM-DD');
                  setWeekRange({ startDate: start, endDate: end });
                }}
              />
            </Box>
          </Grid>
        </Grid>
        {openEditDialog && editAppointment && (
          <EditAppointment
            {...editAppointment}
            loading={editLoading}
            setLoading={setEditLoading}
            onClose={() => {
              setEditAppointment(null);
              setOpenEditDialog(false);
            }}
            onUpdateAppointment={() => {
              setEditAppointment(null);
              setOpenEditDialog(false);
              getAppointmentWeekView();
            }}
          />
        )}
        {openNewAppointment && newAppointment && (
          <NewAppointment
            {...newAppointment}
            loading={newLoading}
            setLoading={setNewLoading}
            onClose={() => {
              setNewAppointment(null);
              setOpenNewAppointment(false);
            }}
            onSaveAppointment={() => {
              setNewAppointment(null);
              setOpenNewAppointment(false);
              getAppointmentWeekView();
            }}
          />
        )}
      </Container>
    </>
  );
};
export default AppointmentCalendar;
